// The module registry, frontend side.
//
// `registry.json` is the mirror of backend/src/core/modules.py and carries only the canonical
// fields — the shape both sides must agree on. This file adds what is presentation and
// therefore lives nowhere else: the Hub tile description, the icon glyph key, whether the tile
// is live, and whether it opens in a new tab.
//
// Keeping the two apart is what lets backend/tools/check_module_registry.py compare the
// canonical set without knowing anything about icons.

import data from './registry.json' with { type: 'json' };

// Tile copy and behaviour, keyed by module id. Split out rather than merged into the JSON so
// the drift checker has an exact field set to compare.
const PRESENTATION = {
  'orbit-ai': { icon: 'chart', live: true, sameTab: true, desc: 'Ask questions using governed Orbit data' },
  'settings': { icon: 'build', live: true, sameTab: true, desc: 'Manage AI connections, profiles and activity' },
  'sql-query-tool':         { icon: 'db',     live: true,  sameTab: true,
    desc: 'Run ad-hoc queries against the dashboard database' },
  'user-management':        { icon: 'users',  live: true,  sameTab: true,
    desc: 'Add users, reset passwords and manage access' },
  'client-management':      { icon: 'clients', live: true, sameTab: true,
    desc: 'Create clients and choose which tools each one appears in' },
  'orbit-development':      { icon: 'build',  live: true,  sameTab: true,
    desc: 'Define development tasks, bugs and feature requests for the Orbit platform' },
  'project-management':     { icon: 'tasks',  live: true,  sameTab: true,
    desc: 'Manage team projects, tasks and collaborate across the team' },
  'reach-tool':             { icon: 'reach',  live: true,  sameTab: true,
    desc: 'Estimate campaign reach and frequency across channels' },
  'paid-media':             { icon: 'paid',   live: false,  sameTab: true,
    desc: 'Bring paid media performance into one client workspace' },
  'taxonomy-generator':     { icon: 'tag',    live: true,  sameTab: true,
    desc: 'Generate and manage campaign taxonomy strings with client-specific field structures' },
  // The two client tools open in their own tab, which is why Competitive Monitoring passes
  // showHub={false} — it is entered directly rather than through the Hub.
  'dashboard-demo':         { icon: 'chart',  live: true,  sameTab: false,
    desc: 'Cross-channel performance dashboard for client presentations' },
  'competitive-monitoring': { icon: 'statex', live: true,  sameTab: false,
    desc: 'Benchmark spend and share of voice against competitors using calibrated Statex data' },
};

export const MODULES = data.modules.map(m => ({ ...m, ...(PRESENTATION[m.id] || {}) }));

export const byId = Object.fromEntries(MODULES.map(m => [m.id, m]));

// Hub section headings, in render order. `group` on each module decides which it lands in.
export const GROUPS = [
  { id: 'admin',    title: 'Administration' },
  { id: 'internal', title: 'Internal Tools' },
  { id: 'client',   title: 'Client Dashboards' },
];

export const isClientScoped = (id) => !!byId[id]?.clientScoped;

/**
 * The module owning an SPA path, or null.
 *
 * Longest match wins, mirroring module_for_route on the backend: '/dashboard-demo/overview'
 * must not be beaten by a shorter prefix, and the comparison is on a segment boundary so
 * '/users' never matches '/users-report'.
 */
export function moduleForPath(pathname) {
  const p = (pathname || '').replace(/\/+$/, '') || '/';
  let best = null;
  for (const m of MODULES) {
    if (!m.path) continue;
    const mp = m.path.replace(/\/+$/, '');
    if ((p === mp || p.startsWith(mp + '/')) && (!best || mp.length > best.path.length)) {
      best = m;
    }
  }
  return best;
}

/**
 * Tiles this user should see, grouped for the Hub.
 *
 * EVERY module requires a grant, including `access: 'open'` ones. That field says a module's
 * API routes are not guarded — a fact about backend enforcement — and it used to double as
 * "always show the tile", which meant Dashboard Demo and Paid Media appeared for people who
 * had been given neither. The Hub is curated in User Management now, so a module nobody
 * granted is a module nobody sees.
 *
 * A user with no grants at all is a normal state — a module admin revoking their only grant
 * leaves them exactly there — so callers must handle an empty result rather than treating it
 * as an error.
 *
 * A global admin sees everything: `may()` on the backend short-circuits for them, so filtering
 * their tiles by grants they do not need to hold would hide tools they can in fact open.
 */
export function visibleModules(grants, isGlobalAdmin = false, actingAs = null) {
  const eligible = MODULES.filter(m => m.access !== 'global-admin' || (isGlobalAdmin && actingAs == null));
  if (isGlobalAdmin) return eligible;
  const held = grants || {};
  return eligible.filter(m => held[m.id] !== undefined);
}
